import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { api } from "@/lib/api"

// ───────────────────────────────────────────────────────────────────────
// Backend contracts (matched to legacy admin's POSTs):
//
//   POST /admin/video_list   { page, limit, search?, sortyBy?, sortDirection? }
//                            → { success, data: { docs, totalDocs, page, limit, totalPages, ... }, message }
//
//   POST /admin/delete_video { id }
//                            → { success, message }
//
//   POST /admin/get_video    { id }
//                            → { success, data: <video>, message }
//
//   POST /admin/update_video (BACKEND, not upload service)
//                            { id, purpose, purpose_id, author, narrater,
//                              tags: string[], proverb,
//                              login_user_sequence, skip_user_sequence }
//                            → { success, data, message }
//
//   POST {UPLOAD_BASE_URL}/set_video (UPLOAD service, NOT /admin/*)
//                            { s3_data, purpose, purpose_id, file_name,
//                              author, narrater, tags: string[], proverb,
//                              is_notification, notification_message }
//                            → { success, message }
//
// NOTE 1: backend expects typo `sortyBy` (extra "y") on list. Preserved.
// NOTE 2: backend expects typo `narrater` (instead of "narrator") on
//         set_video AND update_video. Preserved.
// NOTE 3: legacy POSTs `/set_video` to the UPLOAD service, NOT /admin.
//         Preserved — we use uploadApi here.
// ───────────────────────────────────────────────────────────────────────

export type AdminVideo = {
  _id: string
  title?: string
  narrated_by?: string
  author?: string
  proverb?: string
  purpose?: string
  purpose_id?: string
  login_user_sequence?: number
  skip_user_sequence?: number
  like_count?: number
  share_count?: number
  comment_count?: number
  video_url?: string
  thumb_url?: string
  thumbnail?: string
  Key?: string
  thumbkey?: string
  shareUrl?: string
  shareUrlLocation?: string
  keyword?: string[]
  keyword_text?: string
  is_notification?: boolean
  notification_message?: string
  is_active?: boolean
  is_deleted?: boolean
  created_at?: string
  updated_at?: string
}

export type VideoListParams = {
  page: number
  limit: number
  search?: string
  /** Backend typo preserved: extra "y". */
  sortyBy?: string
  sortDirection?: 1 | -1
}

export type VideoListResponse = {
  docs: AdminVideo[]
  totalDocs: number
  page: number
  limit: number
  totalPages: number
}

const videosListKey = (p: VideoListParams) =>
  ["admin", "videos", "list", p] as const

export function useVideosList(params: VideoListParams) {
  return useQuery({
    queryKey: videosListKey(params),
    queryFn: async (): Promise<VideoListResponse> => {
      const res = await api.post<{
        success: boolean
        data: VideoListResponse
        message: string
      }>("/admin/video_list", params)
      const data = res.data?.data
      return {
        docs: Array.isArray(data?.docs) ? data.docs : [],
        totalDocs: typeof data?.totalDocs === "number" ? data.totalDocs : 0,
        page: typeof data?.page === "number" ? data.page : params.page,
        limit: typeof data?.limit === "number" ? data.limit : params.limit,
        totalPages: typeof data?.totalPages === "number" ? data.totalPages : 0,
      }
    },
    placeholderData: (prev) => prev,
  })
}

export function useDeleteVideo() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await api.post("/admin/delete_video", { id })
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "videos"] })
    },
  })
}

export function useGetVideo(id: string | undefined) {
  return useQuery({
    queryKey: ["admin", "videos", "get", id] as const,
    queryFn: async (): Promise<AdminVideo | null> => {
      if (!id) return null
      const res = await api.post<{
        success: boolean
        data: AdminVideo
        message: string
      }>("/admin/get_video", { id })
      return res.data?.data ?? null
    },
    enabled: !!id,
  })
}

// ───────────────────────────────────────────────────────────────────────
// CREATE — runs after a successful file upload via VideoFileDrop. Posts
// metadata + the s3response we got back from /upload_video to the UPLOAD
// service's /set_video. The legacy admin posts to UPLOADAPI here too.
//
// Field mapping (legacy ↔ v2):
//   s3_data              ← UploadResult.s3response
//   purpose              ← purpose dropdown selection (string label)
//   purpose_id           ← purpose dropdown selection (ObjectId)
//   file_name            ← UploadResult.file_name
//   author               ← author input
//   narrater             ← narrater input  (typo PRESERVED — the backend
//                          field is misspelled in the legacy schema)
//   tags                 ← string[]  (legacy sent `tagtext` — same shape)
//   proverb              ← textarea
//   is_notification      ← boolean
//   notification_message ← textarea (only meaningful when is_notification true)
// ───────────────────────────────────────────────────────────────────────
type SetVideoPayload = {
  s3_data: unknown
  purpose: string
  purpose_id: string
  file_name: string
  author: string
  narrater: string
  tags: string[]
  proverb: string
  is_notification: boolean
  notification_message: string
  /** Optional poster frame extracted in the browser and uploaded to
   *  S3. Backend setvideo reads .Location/.Key (or lowercase) and
   *  persists thumbnail/thumbkey on the video doc. */
  S3_thumbnail?: unknown
}

export function useSetVideo() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: SetVideoPayload) => {
      // BUGFIX: was uploadApi.post("/set_video") → upload.goodtalk.coach,
      // which the browser never reaches (no nginx hit, no record ever
      // created). Use the working API host + the deployed backend
      // route /admin/set_video (creates the video immediately + saves
      // metadata + resolves the coach FK).
      const res = await api.post<{
        success: boolean
        message: string
      }>("/admin/set_video", payload)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "videos"] })
    },
  })
}

// ───────────────────────────────────────────────────────────────────────
// UPDATE — for editing an existing video. Different endpoint, different
// (smaller) field set than CREATE: no S3 data, no notification fields, but
// includes login_user_sequence + skip_user_sequence (which the legacy
// upload form had commented out).
//
// Field mapping (legacy UpdateVideo.jsx ↔ v2):
//   id                   ← URL :id param
//   purpose              ← dropdown label
//   purpose_id           ← dropdown ObjectId
//   author               ← author input
//   narrater             ← narrater input  (typo PRESERVED)
//   tags                 ← string[]  (legacy built `tagsall` from tags[i].text)
//   proverb              ← textarea
//   login_user_sequence  ← number input
//   skip_user_sequence   ← number input
// ───────────────────────────────────────────────────────────────────────
type UpdateVideoPayload = {
  id: string
  purpose: string
  purpose_id: string
  author: string
  narrater: string
  tags: string[]
  proverb: string
  login_user_sequence: number | string
  skip_user_sequence: number | string
}

export function useUpdateVideo() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: UpdateVideoPayload) => {
      const res = await api.post<{
        success: boolean
        data?: AdminVideo
        message: string
      }>("/admin/update_video", payload)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "videos"] })
    },
  })
}
